/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Grid,
  Typography,
  Link as MuiLink,
  Tooltip,
  IconButton,
} from '@mui/material';
import React from 'react';
import { ExitDomain, Link } from '@opengeoweb/theme';
import type { InfoDialogBaseInfoProps } from '../../store/types';
import { useDataExplorerTranslation } from '../../utils/i18n';

const InfoDialogBaseInfo: React.FC<InfoDialogBaseInfoProps> = ({
  properties,
  datasetLink,
  id,
}: InfoDialogBaseInfoProps) => {
  const { t } = useDataExplorerTranslation();
  // Format date and time to a correct format
  // Example: "2023-10-01T12:00:00Z" to "October 1, 2023 (12:00)"
  const formatDateTime = (dateTimeString: string): string => {
    const date = new Date(dateTimeString);

    // Format the date part
    const dateOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    const formattedDate = date.toLocaleDateString(undefined, dateOptions);

    // Format the time part
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false, // Use 24-hour format
    };
    const formattedTime = date.toLocaleTimeString(undefined, timeOptions);

    return `${formattedDate} (${formattedTime})`;
  };

  const formatLanguage = (language: string): string => {
    switch (language) {
      case 'en':
      case 'eng':
        return 'English';
      case 'fi':
        return 'Finnish';
      case 'sv':
        return 'Swedish';
      case 'fr':
        return 'French';
      case 'es':
        return 'Spanish';
      case 'de':
        return 'German';
      default:
        return language;
    }
  };

  const formatNames = (properties.formats ?? [])
    .map((format) => format?.name || format?.mediaType)
    .filter((formatName): formatName is string => !!formatName);

  return (
    <Grid
      container
      data-testid="infoDialogBaseInfo"
      spacing={2}
      style={{ marginLeft: 20, marginTop: 10 }}
      sx={{ flexDirection: 'column' }}
    >
      <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
        <Grid>
          <Typography color="textSecondary" variant="caption">
            {t('info-dialog-name')}
          </Typography>
        </Grid>
        <Grid>
          <Typography>
            {properties.title || properties.identifier || id}
          </Typography>
        </Grid>
      </Grid>
      {!!properties.updated && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('base-info-last-updated')}
            </Typography>
          </Grid>
          <Grid>
            <Typography data-testid="infoDialogBaseInfolastUpdated">
              {formatDateTime(properties.updated)}
            </Typography>
          </Grid>
        </Grid>
      )}
      {!!properties.description && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('base-info-description')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>{properties.description}</Typography>
          </Grid>
        </Grid>
      )}
      {!!properties.language && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('base-info-language')}
            </Typography>
          </Grid>
          <Grid>
            {properties.language.code ? (
              <Typography>
                {formatLanguage(properties.language.code)}
              </Typography>
            ) : (
              <Typography>
                {formatLanguage(properties.language as unknown as string)}
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
      {!!properties['wmo:dataPolicy'] && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('base-info-data-policy')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>
              {properties['wmo:dataPolicy'].charAt(0).toUpperCase() +
                properties['wmo:dataPolicy'].slice(1)}
            </Typography>
          </Grid>
        </Grid>
      )}
      {formatNames.length > 0 && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('base-info-format')}
            </Typography>
          </Grid>
          <Grid>
            {formatNames.map((formatName) => (
              <Typography key={formatName}>{formatName}</Typography>
            ))}
          </Grid>
        </Grid>
      )}
      {!!id && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('base-info-share')}
            </Typography>
          </Grid>
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid size={10}>
              <Typography>{`${datasetLink}/items/${id}`}</Typography>
            </Grid>
            <Grid
              container
              size={2}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-evenly',
              }}
            >
              <MuiLink
                href={`${datasetLink}/items/${id}`}
                rel="noopener"
                target="_blank"
              >
                <Tooltip title={t('Go to the source')}>
                  <IconButton style={{ borderRadius: 50 }}>
                    <ExitDomain />
                  </IconButton>
                </Tooltip>
              </MuiLink>
              <Grid>
                <Tooltip title={t('Copy dataset link')}>
                  <IconButton
                    onClick={() => {
                      navigator.clipboard
                        .writeText(`${datasetLink}/items/${id}`)
                        .catch((error) => {
                          console.error(
                            'Failed to copy text to clipboard:',
                            error,
                          );
                        });
                    }}
                    style={{ borderRadius: 50 }}
                  >
                    <Link />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default InfoDialogBaseInfo;
